import { DOCS_URLS } from "@p0security/frontend-core";
import type {
  GithubComponents,
  RepositoryAccessConfig,
} from "@p0security/integrations-github-shared";
import type { FrontendInstaller } from "@p0security/integrations-installer";
import { Typography } from "@p0security/ui";

import { toOrgAppsUrl } from "./app-registration.ts";
import { CLOUDS, type CloudDispatch, toVaultCloud } from "./clouds.ts";
import { type RenderedCommands, toItemCommands } from "./commands.ts";
import { ItemSteps } from "./item-steps.tsx";

const { Link, Paragraph, Text } = Typography;

/** Says how to run the connector's shell script. It sets `set -e`, which
 * would close an interactive shell it's pasted into at its first error, so
 * it's saved and run with bash. It lets P0 invoke the connector last, so a
 * run that stops early leaves P0 unable to invoke it. */
const ShellHeader: React.FC = () => (
  <Paragraph>
    Save this script as a file, such as <Text code>deploy-connector.sh</Text>,
    and run it with <Text code>bash deploy-connector.sh</Text>. It ends with{" "}
    <Text code>Deployed the connector</Text>. If it stops before that, fix what
    it reports and run it again. Then click &quot;Next&quot;.
  </Paragraph>
);

/** The connector's shell script and Terraform as the instruction's commands,
 * if they rendered. */
const toInstructionCommands = (rendered: RenderedCommands | undefined) =>
  rendered?.terraform === undefined
    ? undefined
    : {
        iac: [{ command: rendered.terraform }],
        shell: [{ command: rendered.shell, header: <ShellHeader /> }],
      };

/** Shown above the new item's form, before the customer has entered
 * anything. */
const NewPreamble: React.FC = () => (
  <>
    <Paragraph>
      P0 grants repository roles, team memberships and force-merge access to
      protected branches in the organization through a connector that you deploy
      in your AWS account or Google Cloud project. The connector acts as a
      GitHub App that you create, and only the connector reads the app&apos;s
      private key.
    </Paragraph>
    <Paragraph>
      To use AWS, have P0&apos;s AWS IAM management and AWS Secrets Manager
      integrations installed, and Terraform, or bash with Docker and the AWS
      CLI. To use Google Cloud, have P0&apos;s Google Cloud IAM management and
      Google Secret Manager integrations installed, and Terraform, or bash with
      the gcloud CLI. Either way, you&apos;ll need an owner of the GitHub
      organization to install the app. See{" "}
      <Link
        href={DOCS_URLS.GITHUB_BEFORE_YOU_BEGIN}
        rel="noopener noreferrer"
        target="_blank"
      >
        Before you begin
      </Link>
      .
    </Paragraph>
    <Paragraph>
      Below, pick where the private key will be stored and where the connector
      will run, both in the same cloud, and name the secret that will hold the
      key. These can&apos;t change once the organization is added. The next page
      shows how to create the GitHub App, save its key in that secret, install
      the app, and deploy the connector.
    </Paragraph>
  </>
);

/** The private key secret field's description, in the words of the item's
 * secret manager. On the new item's form, that's the one picked so far. */
const toSecretDescription = ({
  vault,
}: Partial<Pick<RepositoryAccessConfig, "vault">>) =>
  `${toVaultCloud(vault?.type).vault.secretDescription} The connector's Terraform or shell script grants it read on exactly this secret, so it can't change once the organization is added.`;

/** A connector name field's description, for a connector that runs as
 * `runtime`. */
const toConnectorNameDescription = (runtime: string) =>
  `The name of the connector's ${runtime}, which its Terraform or shell script creates. Each organization needs a connector of its own.`;

/** A region field's description, for what's in `cloud`'s region. */
const toRegionDescription = (
  { label, regionPlaceholder }: CloudDispatch,
  subject: string
) => `The ${label} region ${subject}, such as ${regionPlaceholder}.`;

/**
 * Installs an organization's GitHub access.
 *
 * The framework's new-item form takes the org's login and where the key and
 * the connector go, which can't change after. The item's page then shows the
 * steps to create the GitHub App, save its key, install it and deploy the
 * connector, with the connector's Terraform and shell script, above the
 * "Next" that verifies the item. Its configure page asks for the App ID, and
 * "Finish" validates the install through the connector.
 */
export const githubInstaller: FrontendInstaller<typeof GithubComponents> = {
  components: {
    "repository-access": {
      newPreamble: () => <NewPreamble />,
      // The new item's form describes the org's login, its item id, with the
      // component's labeler.
      labeler: () =>
        "The organization's login, as in github.com/<login>. Each organization gets its own GitHub App and connector.",
      // The item was saved with every field the commands need, so they
      // render from what the item stores.
      instructions: (context, id, item) => {
        const rendered = toItemCommands(context, id, item);
        return {
          help: <ItemSteps item={item} org={id} rendered={rendered} />,
          commands: toInstructionCommands(rendered),
        };
      },
      // The framework passes the configure form's draft, which keeps the App
      // ID entered whether or not "Finish" succeeded, so the steps' state
      // comes from the item P0 stored.
      preamble: (context, id) => {
        const stored = context.config?.["repository-access"]?.[id];
        return stored?.state === "configure" ? (
          <ItemSteps
            item={stored}
            org={id}
            rendered={toItemCommands(context, id, stored)}
          />
        ) : undefined;
      },
      items: {
        appId: {
          labeler: (_context, id) => (
            <>
              Find it on the app&apos;s settings page, under About: open{" "}
              <Link
                href={toOrgAppsUrl(id)}
                rel="noopener noreferrer"
                target="_blank"
              >
                {id}&apos;s GitHub Apps
              </Link>{" "}
              and click Edit beside the app.
            </>
          ),
        },
        privateKeySecretName: {
          labeler: (_context, _id, item) => toSecretDescription(item),
        },
        vault: {
          labeler: () =>
            "Where the GitHub App's private key is stored. Pick the same cloud as the connector hosting.",
          options: {
            "aws-sm": {
              items: {
                install: {
                  labeler: () => CLOUDS.aws.vault.install.description,
                  optionProvider: CLOUDS.aws.vault.install.installs,
                },
                secretsRegion: {
                  labeler: () =>
                    toRegionDescription(CLOUDS.aws, "the secret is in"),
                },
              },
            },
            "gcp-sm": {
              items: {
                install: {
                  labeler: () => CLOUDS.gcp.vault.install.description,
                  optionProvider: CLOUDS.gcp.vault.install.installs,
                },
              },
            },
          },
        },
        hosting: {
          labeler: () =>
            "Where the connector runs. Pick the same cloud as the secret manager provider.",
          options: {
            aws: {
              items: {
                accountId: {
                  labeler: () => CLOUDS.aws.hosting.description,
                  optionProvider: CLOUDS.aws.hosting.installs,
                },
                connectorName: {
                  labeler: () => toConnectorNameDescription(CLOUDS.aws.runtime),
                },
                connectorRegion: {
                  labeler: () =>
                    toRegionDescription(CLOUDS.aws, "the connector runs in"),
                },
              },
            },
            gcp: {
              items: {
                projectId: {
                  labeler: () => CLOUDS.gcp.hosting.description,
                  optionProvider: CLOUDS.gcp.hosting.installs,
                },
                connectorName: {
                  labeler: () => toConnectorNameDescription(CLOUDS.gcp.runtime),
                },
                connectorRegion: {
                  labeler: () =>
                    toRegionDescription(CLOUDS.gcp, "the connector runs in"),
                },
              },
            },
          },
        },
      },
    },
  },
};
